import type { PlanDay, PlanLoose } from '../contracts/items.js'

/**
 * What moved since a traveller last looked at their own itinerary.
 *
 * Ana edits a trip after the link has gone out — a flight is rebooked, Tuesday's
 * restaurant is swapped — and until now the page said nothing. The traveller had
 * a document that changed under them with no way to tell which line was new,
 * which is the failure mode a delivered document is not supposed to have.
 *
 * Lives beside `position.ts` for the same reason that one does: it is pure
 * comparison over a contract type, the interesting parts are the boundaries, and
 * testing it needs no browser.
 *
 * **This is a timestamp comparison and not an audit log, deliberately.** An
 * events table would have to be written on every advisor edit, kept in step with
 * every future write path, and confined by its own RLS — and it would answer the
 * same question `updated_at` already answers, for a table that grows forever. It
 * earns its cost when somebody needs the HISTORY of a trip. Nobody does; the
 * traveller needs "look here".
 *
 * Three things it therefore cannot do, all of them honest limits rather than
 * bugs to fix later:
 *
 *  - **It cannot show a removal.** A deleted row leaves nothing to compare, so a
 *    cancelled dinner disappears silently. Telling the traveller would take a
 *    tombstone, which is the audit log arriving by the back door.
 *  - **It cannot say what changed inside an entry.** "Moved 40 minutes later"
 *    needs the previous value, and the only place to keep one without a server
 *    table is the viewer's own device — where it would be a second copy of the
 *    document that drifts per device, and would state a delta as fact when it is
 *    only that browser's memory. A marker is true regardless of which device it
 *    is read on; a stated delta would be a plausible wrong answer, which this
 *    codebase already knows is the worse failure.
 *  - **It knows nothing on a new device.** `seenAt` is per-browser (see
 *    `PlanUpdates.tsx`), so a first visit has no baseline and reports nothing.
 *    That is the right answer to "what changed since you last looked" when you
 *    have not looked.
 */

/** `added` outranks `edited`: a row created since is new, whatever happened after. */
export type ChangeKind = 'added' | 'edited'

export interface PlanChanges {
  /** Item id → what happened to it. Covers loose items as well as scheduled ones. */
  items: ReadonlyMap<string, ChangeKind>
  /** Day id → what happened to the day's OWN title or note, never to its items. */
  days: ReadonlyMap<string, ChangeKind>
  /** Days plus items. What the banner counts. */
  count: number
}

export const NO_PLAN_CHANGES: PlanChanges = {
  items: new Map(),
  days: new Map(),
  count: 0,
}

interface Stamped {
  id: string
  createdAt: string
  updatedAt: string
}

/**
 * `createdAt` is tested first so a row that appeared AND was then edited reads as
 * `added`, which is the more useful of the two labels. Comparing `createdAt`
 * against `updatedAt` to decide would be the fragile version: it depends on two
 * defaults evaluating to the identical instant, which holds today because both
 * come from `now()` in one statement, and stops holding the moment anything
 * backfills a row.
 */
function classify(row: Stamped, since: number): ChangeKind | null {
  if (Date.parse(row.createdAt) > since) return 'added'
  if (Date.parse(row.updatedAt) > since) return 'edited'
  return null
}

export function changesSince(
  plan: { days: PlanDay[]; loose: PlanLoose },
  seenAt: string | null,
): PlanChanges {
  // No baseline is not "everything is new" — see the third limit above.
  if (seenAt === null) return NO_PLAN_CHANGES
  const since = Date.parse(seenAt)
  // A corrupted or hand-edited storage value must not turn every entry on the
  // page into a change. `NaN` comparisons are all false, so the arithmetic below
  // would quietly report nothing; saying so here makes that a decision.
  if (Number.isNaN(since)) return NO_PLAN_CHANGES

  const days = new Map<string, ChangeKind>()
  const items = new Map<string, ChangeKind>()

  for (const day of plan.days) {
    const dayChange = classify(day, since)
    if (dayChange) days.set(day.id, dayChange)
    for (const item of day.items) {
      const itemChange = classify(item, since)
      if (itemChange) items.set(item.id, itemChange)
    }
  }

  // The unscheduled section is part of the plan, so something added there is
  // something added. It is where "I found you a place, no date yet" lands.
  for (const item of plan.loose.items) {
    const itemChange = classify(item, since)
    if (itemChange) items.set(item.id, itemChange)
  }

  return { days, items, count: days.size + items.size }
}
